import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Shield, Users, FileText, Settings, LogOut, ArrowLeft,
  TrendingUp, DollarSign, AlertTriangle, Activity,
  Loader2
} from 'lucide-react';
import api from '../utils/api';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const data = await api.admin.getStats();
      setStats(data);
    } catch (err) {
      setError('Failed to load dashboard statistics');
      if (err.message?.includes('Session expired') || err.message?.includes('access denied') || err.message?.includes('Access denied')) {
        navigate('/admin-portal-7x9k2m');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('isAdmin');
    navigate('/admin-portal-7x9k2m');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-red-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-600 rounded-lg">
              <Shield className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Admin Portal</h1>
              <p className="text-xs text-gray-400">Due Diligence Platform</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white transition-colors">
              <ArrowLeft className="h-4 w-4" />
              User Portal
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white transition-colors"
            >
              <LogOut className="h-4 w-4" />
              Sign Out
            </button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-gray-800/50 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1">
            <Link
              to="/admin-portal-7x9k2m/dashboard"
              className="px-4 py-3 text-sm font-medium text-white border-b-2 border-red-500"
            >
              Dashboard
            </Link>
            <Link
              to="/admin-portal-7x9k2m/reports"
              className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600"
            >
              Reports
            </Link>
            <Link
              to="/admin-portal-7x9k2m/users"
              className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600"
            >
              Users
            </Link>
            <Link
              to="/admin-portal-7x9k2m/config"
              className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600"
            >
              Configuration
            </Link>
            <Link
              to="/admin-portal-7x9k2m/vouchers"
              className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600"
            >
              Vouchers
            </Link>
            <Link
              to="/admin-portal-7x9k2m/reviews"
              className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600"
            >
              Reviews
            </Link>
            <Link
              to="/admin-portal-7x9k2m/audit"
              className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600"
            >
              Audit
            </Link>
            <Link
              to="/admin-portal-7x9k2m/api-keys"
              className="px-4 py-3 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent hover:border-gray-600"
            >
              API Keys
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-900/50 border border-red-700 rounded-lg text-red-200">
            {error}
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-gray-400 text-sm font-medium">Total Users</h3>
              <Users className="h-5 w-5 text-blue-400" />
            </div>
            <p className="text-3xl font-bold text-white">{stats?.users?.total || 0}</p>
            <p className="text-sm text-gray-500 mt-1">
              {stats?.users?.activeToday || 0} active today
            </p>
          </div>

          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-gray-400 text-sm font-medium">Total Reports</h3>
              <FileText className="h-5 w-5 text-green-400" />
            </div>
            <p className="text-3xl font-bold text-white">{stats?.reports?.total || 0}</p>
            <p className="text-sm text-gray-500 mt-1">
              {stats?.reports?.thisMonth || 0} this month
            </p>
          </div>

          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-gray-400 text-sm font-medium">Revenue (MTD)</h3>
              <DollarSign className="h-5 w-5 text-yellow-400" />
            </div>
            <p className="text-3xl font-bold text-white">
              £{((stats?.revenue?.thisMonth || 0) / 100).toLocaleString()}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              £{((stats?.revenue?.total || 0) / 100).toLocaleString()} total
            </p>
          </div>

          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-gray-400 text-sm font-medium">Avg Risk Score</h3>
              <Activity className="h-5 w-5 text-purple-400" />
            </div>
            <p className="text-3xl font-bold text-white">{stats?.reports?.avgRiskScore || 0}</p>
            <p className="text-sm text-gray-500 mt-1">
              {stats?.reports?.highRisk || 0} high-risk reports
            </p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Link
            to="/admin-portal-7x9k2m/reports"
            className="bg-gray-800 rounded-lg p-6 border border-gray-700 hover:border-red-500 transition-colors group"
          >
            <FileText className="h-8 w-8 text-gray-400 group-hover:text-red-400 mb-3" />
            <h3 className="text-lg font-semibold text-white mb-1">View All Reports</h3>
            <p className="text-sm text-gray-400">Access and download all user reports</p>
          </Link>

          <Link
            to="/admin-portal-7x9k2m/users"
            className="bg-gray-800 rounded-lg p-6 border border-gray-700 hover:border-red-500 transition-colors group"
          >
            <Users className="h-8 w-8 text-gray-400 group-hover:text-red-400 mb-3" />
            <h3 className="text-lg font-semibold text-white mb-1">Manage Users</h3>
            <p className="text-sm text-gray-400">View and manage user accounts</p>
          </Link>

          <Link
            to="/admin-portal-7x9k2m/config"
            className="bg-gray-800 rounded-lg p-6 border border-gray-700 hover:border-red-500 transition-colors group"
          >
            <Settings className="h-8 w-8 text-gray-400 group-hover:text-red-400 mb-3" />
            <h3 className="text-lg font-semibold text-white mb-1">Configuration</h3>
            <p className="text-sm text-gray-400">Manage AI providers and settings</p>
          </Link>
        </div>

        {/* Recent Activity */}
        <div className="bg-gray-800 rounded-lg border border-gray-700">
          <div className="px-6 py-4 border-b border-gray-700">
            <h2 className="text-lg font-semibold text-white">Recent Reports</h2>
          </div>
          <div className="p-6">
            {stats?.recentReports?.length > 0 ? (
              <div className="space-y-4">
                {stats.recentReports.map((report) => (
                  <div key={report._id} className="flex items-center justify-between p-4 bg-gray-700/50 rounded-lg">
                    <div>
                      <p className="font-medium text-white">{report.subjectName}</p>
                      <p className="text-sm text-gray-400">
                        {report.reportType} • {new Date(report.createdAt).toLocaleDateString('en-GB')}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        report.riskScore >= 70 ? 'bg-red-900/50 text-red-300' :
                        report.riskScore >= 40 ? 'bg-yellow-900/50 text-yellow-300' :
                        'bg-green-900/50 text-green-300'
                      }`}>
                        Risk: {report.riskScore}
                      </span>
                      <Link
                        to={`/admin-portal-7x9k2m/reports?id=${report._id}`}
                        className="text-red-400 hover:text-red-300 text-sm"
                      >
                        View
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-center py-8">No recent reports</p>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
